// Project-name → folder-name slug helper.
//
// OPFS accepts arbitrary Unicode in directory names, so we DON'T need to
// transliterate Chinese characters into ASCII (unlike Git). Kenneth's
// projects regularly carry CJK names ("起源儲蓄計劃 v2") and we want
// the OPFS folder to read identically in any future browser-side picker.
//
// What we DO strip / normalize:
//   - filesystem-unsafe glyphs ( / \ : * ? " < > | )
//   - ASCII control characters (0x00–0x1F + 0x7F DEL)
//   - leading / trailing whitespace
//   - leading dots (would create hidden entries on some hosts)
//   - collapse repeated whitespace into single spaces
//   - cap length at 80 chars (OPFS itself allows ~255 but display gets ugly)
//
// Output is still a free-form Unicode string, NOT a kebab-case ASCII slug.
// Spaces and hyphens are preserved — they read more naturally than
// hyphenated transliterations.
// If two projects collide on slug after normalization, the caller appends
// a numeric suffix ("name", "name (2)", "name (3)").

const UNSAFE_GLYPHS = new Set(['\\', '/', ':', '*', '?', '"', '<', '>', '|']);
const COLLAPSE_WS_RE = /\s+/g;

export function slugifyProjectName(raw: string): string {
  // First pass: drop unsafe glyphs and ASCII control chars character-by-
  // character. Doing this with explicit code-point checks avoids embedding
  // control bytes inside the regex source itself, which keeps the file
  // safe to grep / diff in any editor.
  let cleaned = '';
  for (const ch of raw) {
    if (UNSAFE_GLYPHS.has(ch)) continue;
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) continue;
    cleaned += ch;
  }
  let s = cleaned.replace(COLLAPSE_WS_RE, ' ').trim();
  // Strip leading dots — hidden-entry convention varies across hosts and
  // is a footgun more than a feature for project folders.
  s = s.replace(/^\.+/, '').trim();
  if (s.length > 80) s = s.slice(0, 80).trim();
  return s;
}

/**
 * Given a desired name and a list of names already in use, return a
 * unique slug by appending " (N)" where N starts at 2. Returns the
 * original slug if it isn't taken.
 */
export function uniqueProjectSlug(desired: string, taken: readonly string[]): string {
  const base = slugifyProjectName(desired) || 'Untitled';
  if (!taken.includes(base)) return base;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} (${n})`;
    if (!taken.includes(candidate)) return candidate;
  }
  // 999-deep collision is implausible; fall back to timestamped name so
  // we never throw from this helper.
  return `${base} ${Date.now()}`;
}
